import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
  type DropAnimation,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import * as Sentry from '@sentry/react';

export interface DraggableListRow {
  id: number;
  name: string;
}

// A card that's being dragged eases in fast and settles with a slight
// overshoot on drop — this is what makes it read as "picked up" rather than
// just teleporting, the same trick Trello's list/card drag uses.
export const SORT_TRANSITION = { duration: 220, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' };
export const DROP_ANIMATION: DropAnimation = { duration: 280, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' };

export const DragHandle = ({ listeners, attributes }: { listeners: any; attributes: any }) => (
  <Box
    {...listeners}
    {...attributes}
    sx={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'grab',
      color: 'var(--color-text-tertiary)',
      // dnd-kit needs the browser to hand touch gestures to it instead of
      // scrolling the page, otherwise a drag on mobile just scrolls.
      touchAction: 'none',
      px: 'var(--space-2)',
      '&:active': { cursor: 'grabbing' },
    }}
  >
    <DragIndicatorIcon fontSize="small" />
  </Box>
);

const RowCard = ({ name, overlay = false, width }: {
  name: string;
  overlay?: boolean;
  width?: number | null;
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--space-2)',
      py: 'var(--space-3)',
      px: 'var(--space-3)',
      minWidth: 0,
      ...(overlay
        ? {
            width: width ?? undefined,
            backgroundColor: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            boxShadow: '0 14px 28px -6px rgba(0,0,0,0.22), 0 4px 10px rgba(0,0,0,0.12)',
            transform: 'rotate(2deg)',
            cursor: 'grabbing',
          }
        : {}),
    }}
  >
    {overlay && <DragIndicatorIcon fontSize="small" sx={{ color: 'var(--color-text-tertiary)' }} />}
    <Typography sx={{ fontWeight: 700, fontSize: 'var(--text-base)' }} noWrap>
      {name}
    </Typography>
  </Box>
);

const SortableRow = ({
  row,
  renderAction,
}: {
  row: DraggableListRow;
  renderAction?: (row: DraggableListRow) => React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: String(row.id),
    transition: SORT_TRANSITION,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform) }}
      sx={{
        mb: 'var(--space-4)',
        overflow: 'hidden',
        transition: `${SORT_TRANSITION.duration}ms ${SORT_TRANSITION.easing}`,
        ...(isDragging
          ? {
              // What's left behind while the real card floats in the
              // DragOverlay: a dashed slot showing exactly where it'll land.
              border: '2px dashed var(--color-border-strong)',
              borderRadius: 'var(--radius-lg)',
              backgroundColor: 'var(--color-bg-subtle)',
              opacity: 0.5,
            }
          : { border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-bg)' }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // Stala wysokosc wiersza — z akcja (kategorie) i bez (pozycje) karty wygladaja tak samo.
          minHeight: 48,
          visibility: isDragging ? 'hidden' : 'visible',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minWidth: 0, flex: 1 }}>
          <DragHandle listeners={listeners} attributes={attributes} />
          <RowCard name={row.name} />
        </Box>
        {renderAction && <Box sx={{ flexShrink: 0, pr: 'var(--space-2)' }}>{renderAction(row)}</Box>}
      </Box>
    </Box>
  );
};

/**
 * Generic Trello-style drag-to-reorder list: optimistic local reorder on
 * drop, persisted via `onPersist`, rolled back with a toast if it fails.
 * Used for both the category order screen and the per-category dish order
 * screen — same interaction, different data source and endpoint.
 */
export const DraggableList = ({
  initialRows,
  onPersist,
  saveErrorMessage,
  renderAction,
}: {
  initialRows: DraggableListRow[];
  onPersist: (orderedIds: number[]) => Promise<void>;
  saveErrorMessage: string;
  renderAction?: (row: DraggableListRow) => React.ReactNode;
}) => {
  const notify = useNotify();
  const [rows, setRows] = React.useState(initialRows);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [activeWidth, setActiveWidth] = React.useState<number | null>(null);

  // A drag only starts after a small pointer move / touch hold, so a plain
  // tap on the handle (or the row underneath it) never gets mistaken for one.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  const activeRow = activeId ? rows.find((row) => String(row.id) === activeId) : undefined;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    setActiveWidth(event.active.rect.current.initial?.width ?? null);
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setActiveWidth(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveId(null);
    setActiveWidth(null);

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = rows.findIndex((row) => String(row.id) === String(active.id));
    const newIndex = rows.findIndex((row) => String(row.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const previous = rows;
    const reordered = arrayMove(rows, oldIndex, newIndex);
    setRows(reordered);

    try {
      await onPersist(reordered.map((row) => row.id));
    } catch (e) {
      console.error('Failed to save order', e);
      Sentry.captureException(e);
      setRows(previous);
      notify(saveErrorMessage, { type: 'error' });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={rows.map((row) => String(row.id))} strategy={verticalListSortingStrategy}>
        {rows.map((row) => (
          <SortableRow key={row.id} row={row} renderAction={renderAction} />
        ))}
      </SortableContext>

      <DragOverlay dropAnimation={DROP_ANIMATION}>
        {activeRow && <RowCard name={activeRow.name} overlay width={activeWidth} />}
      </DragOverlay>
    </DndContext>
  );
};

export default DraggableList;
